// JS
import React from 'react';
import Button from '../Button';
import { randomId, renderHTML } from '../shared/utils';
import Slider from '../Slider';
import { Skeleton } from '../Ui/skeleton';

// COMPONENT
const Hero = ({
  data,
  topMargin = true,
  singleHero = false,
  gaBlockName,
  autoplay = true,
  showHero = true,
  salePromo = false,
  heroOptions
}) => {
  const uniqueId = randomId();
  const getTitleSubtitle = (title, subtitle) =>
    [title, subtitle].filter(Boolean).join(' ');

  return (
    <section
      id={'hero-' + uniqueId}
      className={
        showHero
          ? (topMargin ? 'my-24 lg:my-48' : 'mb-16 lg:mb-32') +
            (heroOptions?.classes ? ' ' + heroOptions.classes : '')
          : 'hidden'
      }
    >
      {data ? (
        <Slider
          name="hero-swiper"
          useNavigation={!singleHero}
          usePagination={!singleHero}
          useFade={true}
          useAutoplay={autoplay}
        >
          {data &&
            data.map((slide, index) => (
              <div
                key={index}
                className={`relative overflow-hidden z-0 bg-cover bg-center flex h-[600px] lg:h-[680px] items-end  ${
                  !singleHero ? 'hover:cursor-grab active:cursor-grabbing' : ''
                }`}
                data-name={getTitleSubtitle(
                  slide.block_title,
                  slide.block_sub_title
                )}
                data-creative={gaBlockName}
                data-id={slide.contentguid}
                data-position={'banner-slot-' + (index + 1)}
                data-type="banner"
              >
                {(() => {
                  const icon = slide.icon;
                  const dskImage = slide.dsk_image;
                  const isVideo =
                    typeof icon === 'string' && /\.(mp4|webm)$/i.test(icon);
                  const isGif =
                    typeof icon === 'string' && /\.gif$/i.test(icon);
                  const hasDesktopImage = !!dskImage;
                  const renderPicture = (
                    imgSrc,
                    extraClass = '',
                    hideOnMobile = false
                  ) => (
                    <picture
                      className={`w-full h-full${
                        hideOnMobile ? ' hidden lg:block' : ''
                      }`}
                    >
                      {dskImage && (
                        <source media="(min-width: 1024px)" srcSet={dskImage} />
                      )}
                      <img
                        className={
                          extraClass ||
                          'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full object-cover !max-w-none !w-auto'
                        }
                        src={imgSrc}
                        alt={getTitleSubtitle(
                          slide.block_title,
                          slide.block_sub_title
                        )}
                      />
                    </picture>
                  );
                  if (isVideo) {
                    const videoClass = hasDesktopImage
                      ? 'absolute inset-0 w-full h-full object-cover !max-w-none lg:hidden'
                      : 'absolute inset-0 w-full h-full object-cover !max-w-none';
                    return (
                      <>
                        <video
                          className={videoClass}
                          src={icon}
                          autoPlay
                          loop
                          muted
                          playsInline
                        />
                        {hasDesktopImage && renderPicture(dskImage, '', true)}
                      </>
                    );
                  }
                  if (isGif) {
                    return (
                      <>
                        <img
                          className={
                            hasDesktopImage
                              ? 'absolute inset-0 w-full h-full object-cover !max-w-none lg:hidden'
                              : 'absolute inset-0 w-full h-full object-cover !max-w-none'
                          }
                          src={icon}
                          alt={getTitleSubtitle(
                            slide.block_title,
                            slide.block_sub_title
                          )}
                        />
                        {hasDesktopImage && renderPicture(dskImage, '', true)}
                      </>
                    );
                  }
                  // Default behaviour: No Video or Gif
                  return renderPicture(icon);
                })()}
                <a
                  alt={getTitleSubtitle(
                    slide.block_title,
                    slide.block_sub_title
                  )}
                  className="after:absolute after:inset-0 after:z-10 after:w-full after:h-full hover:text-current"
                  href={slide.url_raw}
                  data-name={getTitleSubtitle(
                    slide.block_title,
                    slide.block_sub_title
                  )}
                  data-creative={gaBlockName}
                  data-id={slide.contentguid}
                  data-position={'button-slot-' + (index + 1)}
                  data-type="banner-button"
                ></a>
                <div
                  className={
                    'after:absolute after:inset-0 after:z-0 after:w-full after:h-full after:bg-gradient-to-t ' +
                    (slide.alternative_hero_colour &&
                    slide.alternative_hero_colour
                      ? 'after:from-black/40 lg:after:from-black/20'
                      : 'after:from-white/50 lg:after:from-white/20')
                  }
                ></div>
                <div
                  className={
                    'absolute left-0 bottom-0 w-full my-10 lg:my-12 uppercase ' +
                    (slide.alternative_hero_colour &&
                    slide.alternative_hero_colour
                      ? 'text-white'
                      : 'text-black')
                  }
                >
                  <div
                    className={`container mx-auto flex flex-col md:flex-row md:items-center md:justify-between ${
                      salePromo && 'md:!items-end'
                    }`}
                  >
                    <div
                      className={`text-lg lg:text-xl mb-10 md:mb-0 ${
                        salePromo && 'mb-16 !text-2xl lg:!text-6xl'
                      }`}
                    >
                      {slide.block_title && (
                        <h2
                          className={`font-bold tracking-widest ${
                            slide.block_description && 'mb-6 md:mb-8'
                          }`}
                        >
                          {slide.block_title}
                          {slide.block_sub_title && (
                            <p className="font-base text-lg lg:text-xl">
                              {slide.block_sub_title}
                            </p>
                          )}
                        </h2>
                      )}
                      {slide.block_description &&
                        renderHTML(slide.block_description, 'text-sm')}
                    </div>
                    <div className="flex items-center justify-center gap-2 flex-col lg:flex-row">
                      {[
                        {
                          url: slide.url_raw,
                          label: slide.link_label,
                          slot: index + 1
                        },
                        slide.url2 && slide.link_label_2
                          ? {
                              url: slide.url2_raw,
                              label: slide.link_label_2,
                              slot: index + 2
                            }
                          : null
                      ]
                        .filter(Boolean)
                        .map((btn, btnIdx) => (
                          <Button
                            key={btn.slot}
                            buttonColour={
                              slide.alternative_hero_colour &&
                              slide.alternative_hero_colour
                                ? 'white'
                                : 'black'
                            }
                            buttonLink={btn.url}
                            buttonLinkText={btn.label}
                            gaBlockTitle={btn.label}
                            gaBlockName={gaBlockName}
                            gaBlockId={slide.contentguid}
                            buttonSlotNumber={btn.slot}
                            buttonAdditionalClasses={{
                              classes: 'z-20'
                            }}
                            buttonOptions={{
                              classes: {
                                wrapper: 'w-full md:w-auto'
                              }
                            }}
                          />
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </Slider>
      ) : (
        <Skeleton className="aspect-square w-full h-[600px] lg:h-[680px]" />
      )}
    </section>
  );
};

export default Hero;
